// Pure notification helpers (backend/docs/notifications.md): stream event
// parsing, link handling, TanStack Query cache updates and preference patches.
// Kept free of React so they are unit tested.

import type { InfiniteData } from "@tanstack/react-query";

import type { SseEvent } from "./sse";
import type {
  Notification,
  NotificationKind,
  NotificationPreference,
  NotificationPreferencePatch,
  Organization,
  Paged,
} from "./types";

export const NOTIFICATION_KINDS: readonly NotificationKind[] = [
  "post.published",
  "post.failed",
  "channel.reconnect",
  "comment.created",
  "preview.feedback",
  "member.joined",
  "trial.ending",
  "payment.failed",
  "rss.error",
];

/** Kinds that also pop a toast when they arrive live (active workspace only). */
export const TOAST_KINDS: ReadonlySet<string> = new Set([
  "post.failed",
  "payment.failed",
  "channel.reconnect",
]);

/** Kinds that are problems: shown with an error tone and announced as such. */
export const PROBLEM_KINDS: ReadonlySet<string> = new Set([
  "post.failed",
  "payment.failed",
  "channel.reconnect",
  "rss.error",
]);

export const NOTIFICATIONS_PER_PAGE = 20;

export type NotificationTab = "all" | "unread";

// ---- stream events -----------------------------------------------------------------

export type StreamMessage =
  | { type: "notification"; notification: Notification; unread: number | null }
  | { type: "unread"; unread: number };

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function count(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
}

/** A list row from the API or the stream; null when it is not one. */
export function toNotification(raw: unknown): Notification | null {
  if (!isObject(raw)) return null;
  if (typeof raw.id !== "string" || !raw.id || typeof raw.kind !== "string") return null;
  if (typeof raw.created_at !== "string") return null;

  return {
    id: raw.id,
    kind: raw.kind,
    title: typeof raw.title === "string" ? raw.title : "",
    body: typeof raw.body === "string" ? raw.body : "",
    link: typeof raw.link === "string" && raw.link ? raw.link : null,
    created_at: raw.created_at,
    read_at: typeof raw.read_at === "string" ? raw.read_at : null,
  };
}

/** Parses an SSE event of the notification stream; unknown or malformed events give null. */
export function parseStreamEvent(e: SseEvent): StreamMessage | null {
  let data: unknown;
  try {
    data = JSON.parse(e.data);
  } catch {
    return null;
  }
  if (!isObject(data)) return null;

  if (e.event === "notification") {
    const notification = toNotification(data.notification);
    if (!notification) return null;

    return { type: "notification", notification, unread: count(data.unread) };
  }

  if (e.event === "unread") {
    const unread = count(data.unread);

    return unread === null ? null : { type: "unread", unread };
  }

  return null;
}

// ---- links -----------------------------------------------------------------------------

export type NotificationTarget = { type: "internal"; to: string } | { type: "external"; href: string };

/**
 * Where a notification leads. App paths ("/o/…") open in the app; http(s) URLs
 * on another origin (a published post on the network) open in a new tab.
 * Anything else (javascript:, data:, protocol-relative) is not followed.
 */
export function notificationTarget(
  link: string | null | undefined,
  origin: string,
): NotificationTarget | null {
  if (!link) return null;
  const l = link.trim();
  if (!l) return null;

  if (l.startsWith("/") && !l.startsWith("//") && !l.startsWith("/\\")) {
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(l)) return null;

    return { type: "internal", to: l };
  }

  let url: URL;
  try {
    url = new URL(l);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.origin === origin) return { type: "internal", to: `${url.pathname}${url.search}${url.hash}` };

  return { type: "external", href: url.toString() };
}

// ---- query cache ---------------------------------------------------------------------------

export type NotificationPages = InfiniteData<Paged<Notification>, number>;

/** Every loaded row once, newest first (a live insert can shift a row onto the next page). */
export function flattenPages(data: NotificationPages | undefined): Notification[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: Notification[] = [];
  for (const page of data.pages) {
    for (const n of page.items) {
      if (seen.has(n.id)) continue;
      seen.add(n.id);
      out.push(n);
    }
  }

  return out;
}

/** The next page to load, or undefined when everything is loaded. */
export function nextPageParam(last: Paged<Notification>, pages: Paged<Notification>[]): number | undefined {
  const loaded = pages.reduce((n, p) => n + p.items.length, 0);
  if (last.items.length === 0 || loaded >= last.meta.total) return undefined;

  return (last.meta.page || pages.length) + 1;
}

/**
 * Adds a live notification to the top of a list. A row already present is left
 * as it is; the Unread tab only takes unread rows.
 */
export function insertNotification(
  data: NotificationPages | undefined,
  n: Notification,
  tab: NotificationTab,
): NotificationPages | undefined {
  if (!data || data.pages.length === 0) return data;
  if (tab === "unread" && n.read_at) return data;
  if (data.pages.some((p) => p.items.some((x) => x.id === n.id))) return data;

  const [first, ...rest] = data.pages;
  if (!first) return data;

  return {
    ...data,
    pages: [
      { ...first, items: [n, ...first.items], meta: { ...first.meta, total: first.meta.total + 1 } },
      ...rest.map((p) => ({ ...p, meta: { ...p.meta, total: p.meta.total + 1 } })),
    ],
  };
}

/** Sets one row's read state everywhere it is loaded. */
export function setReadState(
  data: NotificationPages | undefined,
  id: string,
  readAt: string | null,
): NotificationPages | undefined {
  if (!data) return data;
  let changed = false;
  const pages = data.pages.map((p) => {
    if (!p.items.some((n) => n.id === id && n.read_at !== readAt)) return p;
    changed = true;

    return { ...p, items: p.items.map((n) => (n.id === id ? { ...n, read_at: readAt } : n)) };
  });

  return changed ? { ...data, pages } : data;
}

/** Marks every loaded row read (rows stay in the Unread tab until it is reloaded). */
export function markAllLoadedRead(data: NotificationPages | undefined, readAt: string) {
  if (!data) return data;

  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((n) => (n.read_at ? n : { ...n, read_at: readAt })),
    })),
  };
}

/** The unread count after one row changed state, never below zero. */
export function adjustUnread(current: number | undefined, wasUnread: boolean, nowUnread: boolean) {
  const c = current ?? 0;
  if (wasUnread === nowUnread) return c;

  return Math.max(0, c + (nowUnread ? 1 : -1));
}

/** Remembers recent ids so a notification toasts and is announced once, even after a reconnect. */
export class RecentIds {
  private readonly ids = new Set<string>();

  constructor(private readonly max = 200) {}

  /** true the first time an id is seen */
  add(id: string) {
    if (this.ids.has(id)) return false;
    this.ids.add(id);
    if (this.ids.size > this.max) {
      const oldest = this.ids.values().next().value;
      if (oldest !== undefined) this.ids.delete(oldest);
    }

    return true;
  }
}

// ---- preferences ------------------------------------------------------------------------------

/** Admin-only kinds are listed only to people who are an owner or admin in some workspace. */
export function isAdminSomewhere(orgs: Pick<Organization, "role">[] | undefined) {
  return (orgs ?? []).some((o) => o.role === "owner" || o.role === "admin");
}

export function visiblePreferences(kinds: NotificationPreference[], adminSomewhere: boolean) {
  return kinds.filter((k) => k.audience !== "admins" || adminSomewhere);
}

export type PreferenceChannel = "in_app" | "email";

/** The partial PUT body for one switch. */
export function preferencePatch(
  kind: string,
  channel: PreferenceChannel,
  value: boolean,
): NotificationPreferencePatch {
  return { [kind]: { [channel]: value } };
}

/** Applies a partial update to the list (optimistic UI); unknown kinds are ignored. */
export function applyPreferencePatch(
  kinds: NotificationPreference[],
  patch: NotificationPreferencePatch,
): NotificationPreference[] {
  return kinds.map((k) => {
    const p = patch[k.kind];
    if (!p) return k;

    return {
      ...k,
      in_app: p.in_app ?? k.in_app,
      email: p.email ?? k.email,
    };
  });
}
